import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { callGenericPersonalizationConfigApi, callPersonalizationConfigApi } from "../../Context/personalizationApi";
import { validatePersonalizationConfig, logPersonalizationError } from "../../Utility/personalizationEngine";

// Generic personalization applies to every visitor (guest and logged-in
// alike) — no memberIdentifier needed, fetched once on initial Home Page
// load regardless of login state. Never rejects: any fetch/parse failure
// falls back to "no personalization" so the default Home Page always renders.
export const fetchGenericPersonalizationConfig = createAsyncThunk(
  "personalization/fetchGenericConfig",
  async ({ clientCode } = {}) => {
    try {
      const rawJson = await callGenericPersonalizationConfigApi(clientCode);
      if (!rawJson) return { rules: [] };

      const { rules } = validatePersonalizationConfig(rawJson);
      return { rules };
    } catch (error) {
      logPersonalizationError("generic-config-fetch-failed", { clientCode, error: error?.message });
      return { rules: [] };
    }
  }
);

// Member-specific personalization — one file per member, only fetched
// post-login once the memberIdentifier is known. A member with no file at
// all just resolves to no member rules (generic still applies).
export const fetchPersonalizationConfig = createAsyncThunk(
  "personalization/fetchMemberConfig",
  async ({ memberIdentifier, clientCode } = {}) => {
    try {
      const rawJson = await callPersonalizationConfigApi(clientCode, memberIdentifier);
      if (!rawJson) return { rules: [] };

      const { rules } = validatePersonalizationConfig(rawJson);
      return { rules };
    } catch (error) {
      logPersonalizationError("member-config-fetch-failed", { memberIdentifier, clientCode, error: error?.message });
      return { rules: [] };
    }
  }
);

export const personalizationSlice = createSlice({
  name: "personalizationSlice",
  initialState: {
    generic: { status: "idle", rules: [] },
    member: { status: "idle", rules: [] },
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchGenericPersonalizationConfig.pending, (state) => {
        state.generic.status = "loading";
      })
      .addCase(fetchGenericPersonalizationConfig.fulfilled, (state, { payload }) => {
        state.generic.status = "succeeded";
        state.generic.rules = payload?.rules || [];
      })
      .addCase(fetchGenericPersonalizationConfig.rejected, (state) => {
        state.generic.status = "failed";
        state.generic.rules = [];
      })
      .addCase(fetchPersonalizationConfig.pending, (state) => {
        state.member.status = "loading";
      })
      .addCase(fetchPersonalizationConfig.fulfilled, (state, { payload }) => {
        state.member.status = "succeeded";
        state.member.rules = payload?.rules || [];
      })
      .addCase(fetchPersonalizationConfig.rejected, (state) => {
        state.member.status = "failed";
        state.member.rules = [];
      });
  },
});

export default personalizationSlice.reducer;
